import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { SpotlightCoords } from '@/lib/tutorials/engine';
import { zIndex } from '@/styles/variants';

interface ScrollLockState {
  scrollY: number;
  bodyStyles: {
    position: string;
    top: string;
    left: string;
    right: string;
    overflow: string;
  };
}

interface TutorialOverlayProps {
  /** Target element selector to spotlight */
  targetSelector: string;

  /** Child components (tooltip, etc.) */
  children?: ReactNode;

  /** Selectors of elements that are allowed to be clicked during tutorial */
  allowedSelectors?: string[];

  /** Called when user clicks outside allowed areas */
  onClickBlocked?: () => void;

  /** Called when target element is not found */
  onTargetNotFound?: (selector: string) => void;

  /** Whether the overlay should be invisible (for blocking interactions only) */
  invisible?: boolean;
}

export function TutorialOverlay({
  targetSelector,
  children,
  allowedSelectors = [],
  onClickBlocked,
  onTargetNotFound,
  invisible = false,
}: TutorialOverlayProps) {
  const [spotlightCoords, setSpotlightCoords] = useState<SpotlightCoords | null>(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [isPositioning, setIsPositioning] = useState(true);
  const scrollLockStateRef = useRef<ScrollLockState | null>(null);

  // Lock body scroll when tutorial overlay is active (works on iOS Safari)
  useEffect(() => {
    // Only lock if not already locked (e.g., by a modal)
    if (document.body.style.position === 'fixed') {
      return;
    }

    const scrollY = window.scrollY;
    scrollLockStateRef.current = {
      scrollY,
      bodyStyles: {
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        overflow: document.body.style.overflow,
      },
    };

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      if (scrollLockStateRef.current) {
        const { scrollY, bodyStyles } = scrollLockStateRef.current;
        document.body.style.position = bodyStyles.position;
        document.body.style.top = bodyStyles.top;
        document.body.style.left = bodyStyles.left;
        document.body.style.right = bodyStyles.right;
        document.body.style.overflow = bodyStyles.overflow;
        window.scrollTo(0, scrollY);
        scrollLockStateRef.current = null;
      }
    };
  }, []);

  // Calculate spotlight position
  const updateSpotlightPosition = useCallback(() => {
    // Special case: if targetSelector is 'none', show full-page overlay without spotlight
    if (targetSelector === 'none') {
      setSpotlightCoords(null);
      setTargetMissing(false);
      setIsPositioning(false);
      return;
    }

    const elements = Array.from(document.querySelectorAll<HTMLElement>(targetSelector));
    if (elements.length === 0) {
      console.warn(`Tutorial target not found: ${targetSelector}`);
      setSpotlightCoords(null);
      setTargetMissing(true);
      setIsPositioning(false);
      onTargetNotFound?.(targetSelector);
      return;
    }

    setTargetMissing(false);
    // If multiple elements match, compute the union bounding box
    const rects = elements.map((el) => el.getBoundingClientRect());
    const top = Math.min(...rects.map((r) => r.top));
    const left = Math.min(...rects.map((r) => r.left));
    const right = Math.max(...rects.map((r) => r.right));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    setSpotlightCoords({ top, left, width: right - left, height: bottom - top });
    setIsPositioning(false);
  }, [targetSelector, onTargetNotFound]);

  // Clear spotlight and recalculate when target changes
  useEffect(() => {
    // Immediately hide spotlight when target changes
    setIsPositioning(true);
    setSpotlightCoords(null);

    let observer: MutationObserver | null = null;
    let retryCount = 0;
    const maxRetries = 20; // Try for ~1 second (20 * 50ms)

    const tryPositioning = () => {
      const element = document.querySelector<HTMLElement>(targetSelector);

      if (element || targetSelector === 'none') {
        // Element exists, position the spotlight
        updateSpotlightPosition();
        if (observer) {
          observer.disconnect();
        }
      } else {
        // Element doesn't exist yet
        retryCount++;

        if (retryCount < maxRetries) {
          // Keep retrying with requestAnimationFrame
          requestAnimationFrame(tryPositioning);
        } else {
          // Give up after max retries
          console.warn(`Tutorial target not found after ${maxRetries} attempts: ${targetSelector}`);
          updateSpotlightPosition(); // This will set targetMissing to true
        }
      }
    };

    // Start trying to position
    requestAnimationFrame(tryPositioning);

    // Also set up a MutationObserver to detect when the element is added to the DOM
    observer = new MutationObserver(() => {
      const element = document.querySelector<HTMLElement>(targetSelector);
      if (element) {
        updateSpotlightPosition();
        observer?.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Update position on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(updateSpotlightPosition);
    };

    window.addEventListener('scroll', handleUpdate, true); // Capture phase
    window.addEventListener('resize', handleUpdate);

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [updateSpotlightPosition, targetSelector]);

  // Block ALL user interactions outside allowed areas
  useEffect(() => {
    const handleEvent = (event: Event) => {
      const target = event.target as HTMLElement;

      // Allow programmatically-dispatched events (not user-triggered)
      // Synthetic events from the tutorial engine have isTrusted === false
      if ('isTrusted' in event && (event as unknown as { isTrusted: boolean }).isTrusted === false) {
        return;
      }

      // Allow tutorial-initiated programmatic clicks (marked by DOMAdapter)
      if (
        event instanceof MouseEvent &&
        (target as HTMLElement & { _tutorialClick?: boolean })._tutorialClick
      ) {
        return;
      }

      // Always allow events on tutorial UI itself
      if (target.closest('[data-tutorial-ui]')) {
        return;
      }

      // Check if event is on an allowed element
      const isAllowed = allowedSelectors.some((selector) => {
        return target.matches(selector) || target.closest(selector);
      });

      if (!isAllowed) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (event.type === 'click') {
          onClickBlocked?.();
        }
      }
    };

    // Block all interaction events in capture phase
    const eventTypes = [
      'click',
      'mousedown',
      'mouseup',
      'dblclick',
      'contextmenu',
      'keydown',
      'keyup',
      'keypress',
      'input',
      'change',
      'focus',
      'blur',
      'touchstart',
      'touchend',
      'touchmove',
    ];

    eventTypes.forEach((eventType) => {
      document.addEventListener(eventType, handleEvent, { capture: true });
    });

    return () => {
      eventTypes.forEach((eventType) => {
        document.removeEventListener(eventType, handleEvent, { capture: true });
      });
    };
  }, [allowedSelectors, onClickBlocked]);

  // If target is missing (error state), still render children (tooltip will show error)
  // but don't render spotlight
  if (targetMissing) {
    return createPortal(
      <div style={{ position: 'relative', zIndex: zIndex.tutorialTooltip }}>{children}</div>,
      document.body
    );
  }

  // Don't render spotlight while positioning to avoid flash of old position
  if (isPositioning) {
    return createPortal(
      <div style={{ position: 'relative', zIndex: zIndex.tutorialTooltip }}>{children}</div>,
      document.body
    );
  }

  // Special case: full-page overlay without spotlight (targetSelector === 'none')
  if (!spotlightCoords) {
    return createPortal(
      <>
        {/* Full-page overlay without spotlight - invisible if used for blocking only */}
        {!invisible && (
          <div
            className="fixed inset-0 pointer-events-none"
            style={{
              backgroundColor: 'rgba(15, 23, 42, 0.6)',
              zIndex: zIndex.tutorialSpotlight,
            }}
            aria-hidden="true"
          />
        )}

        {/* Children (tooltip, etc.) */}
        <div style={{ position: 'relative', zIndex: zIndex.tutorialTooltip }}>{children}</div>
      </>,
      document.body
    );
  }

  return createPortal(
    <>
      {/* Spotlight with darkening effect via box-shadow */}
      <div
        className="fixed pointer-events-none rounded-lg"
        style={{
          top: spotlightCoords.top - 4,
          left: spotlightCoords.left - 4,
          width: spotlightCoords.width + 8,
          height: spotlightCoords.height + 8,
          boxShadow: `0 0 0 9999px rgba(15, 23, 42, 0.6)`,
          zIndex: zIndex.tutorialSpotlight,
          animation: 'tutorialFadeIn 0.3s ease-in',
        }}
        aria-hidden="true"
      />

      {/* Children (tooltip, etc.) */}
      <div style={{ position: 'relative', zIndex: zIndex.tutorialTooltip }}>{children}</div>

      {/* Add keyframes for fade-in animation */}
      <style>{`
        @keyframes tutorialFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>,
    document.body
  );
}
